import React from 'react';
import { LockedInfoIcon } from '../../../../controls/icons/LockedInfoIcon';
import { ProcedureRow, ProcedureRowData } from '../ProcedureRow';

interface DetailsFormStepProps {
  formRef: React.RefObject<HTMLFormElement>;
  state: {
    caseId: string;
    date: string;
    isPrimary: boolean | null;
    isNavigated: boolean | null;
    procedures: ProcedureRowData[];
    caseIdError: string | null;
    procedureError: string | null;
    isStaged?: boolean | null;
    hasPrevious?: boolean | null;
  };
  actions: {
    setCaseId: (val: string) => void;
    setDate: (val: string) => void;
    setIsPrimary: (val: boolean) => void;
    setIsNavigated: (val: boolean) => void;
    handleSubmit: (e: React.FormEvent) => void;
    updateProcedure: (id: string, field: keyof Omit<ProcedureRowData, 'id'>, val: string) => void;
    removeProcedure: (id: string) => void;
  };
}

export const DetailsFormStep: React.FC<DetailsFormStepProps> = ({ state, actions, formRef }) => {
  const isCaseIdLocked = state.isStaged === true && state.hasPrevious === true;
  return (
    <form ref={formRef} onSubmit={actions.handleSubmit} className="flex-1 overflow-y-auto space-y-8" noValidate>
      <div className="grid grid-cols-1 gap-5 mx-auto justify-items-stretch">

        {/* Case ID */}
        <div className="form-field-container">
          <label htmlFor="caseId" className="block mb-1 text-sm font-medium text-gray-700">
            Case ID Number <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col gap-1">
            <div className="relative w-[180px]">
              <input
                id="caseId"
                type="text"
                autoComplete="off"
                required
                data-testid="input-case-id"
                value={state.caseId}
                onChange={e => !isCaseIdLocked && actions.setCaseId(e.target.value)}
                disabled={isCaseIdLocked}
                aria-disabled={isCaseIdLocked}
                placeholder="e.g. 1234"
                className={`
                  h-[30px] w-full px-3 pr-9 border rounded-md text-sm transition-all
                  ${state.caseIdError ? 'invalid border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'}
                `}
              />

              {/* Tooltip Icon - Black and Hover-Triggered */}
              {isCaseIdLocked && (
                <div 
                  className="absolute inset-y-0 right-2 flex items-center pointer-events-auto"
                  title="Locked from staged selection"
                >
                  <LockedInfoIcon className="w-[18px] h-[18px] text-[#1a1a1b]" aria-hidden="true" />
                </div>
              )}
            </div>

            {state.caseIdError && (
              <p className="text-xs text-red-600 mt-1">{state.caseIdError}</p>
            )}
          </div>
        </div>

        {/* Date */}
        <div className="form-field-container">
          <label htmlFor="startDate">Case Date <span className="text-red-500">*</span></label>
          <input
            id="startDate" type="date" required
            data-testid="input-start-date"
            value={state.date}
            onChange={e => actions.setDate(e.target.value)}
            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-[30px] w-[180px]"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Primary / Revision */}
        <div className="form-field-container" data-testid="is-primary-select">
          <label>Was this a primary or a revision case? <span className="text-red-500">*</span></label>
          <div className="ml-2 mt-2 flex flex-wrap gap-x-6 gap-y-2">
            <div className="flex items-center">
              <input
                id="isPrimaryCase" name="caseType" type="radio" className="custom-radio-input"
                data-testid="radio-not-revision"
                checked={state.isPrimary === true} onChange={() => actions.setIsPrimary(true)}
              />
              <label htmlFor="isPrimaryCase" className="custom-radio-label">Primary</label>
            </div>
            <div className="flex items-center">
              <input
                id="isRevisionCase" name="caseType" type="radio" className="custom-radio-input"
                data-testid="radio-revision"
                checked={state.isPrimary === false} onChange={() => actions.setIsPrimary(false)}
              />
              <label htmlFor="isRevisionCase" className="custom-radio-label">Revision</label>
            </div>
          </div>
        </div>

        {/* Navigated / Recording */}
        <div className="form-field-container" data-testid="is-navigated-select">
          <label>Was the case navigated or data recording only? <span className="text-red-500">*</span></label>
          <div className="ml-2 mt-2 flex flex-wrap gap-x-6 gap-y-2">
            <div className="flex items-center">
              <input
                id="isNavigatedCase" name="navType" type="radio" className="custom-radio-input"
                data-testid="radio-navigated"
                checked={state.isNavigated === true} onChange={() => actions.setIsNavigated(true)}
              />
              <label htmlFor="isNavigatedCase" className="custom-radio-label">Navigated</label>
            </div>
            <div className="flex items-center">
              <input
                id="isRecordingOnlyCase" name="navType" type="radio" className="custom-radio-input"
                data-testid="radio-not-navigated"
                checked={state.isNavigated === false} onChange={() => actions.setIsNavigated(false)}
              />
              <label htmlFor="isRecordingOnlyCase" className="custom-radio-label">Data Recording Only</label>
            </div>
          </div>
        </div>
      </div>

      {/* Procedures */}
      <div className="space-y-4 pb-6" data-testid="case_procedure_details">
        <div className="text-left">Please specify the surgical levels and procedure type(s).</div>
        {state.procedures.map((row, index) => (
          <ProcedureRow
            key={row.id}
            data={row}
            onUpdate={actions.updateProcedure}
            onRemove={actions.removeProcedure}
            showAddButton={index === state.procedures.length - 1}
            showRemoveButton={state.procedures.length > 1}
            showLabels={index === 0}
          />
        ))}
        {state.procedureError && (
          <div className="bg-red-100 text-red-700 p-4 m-6 rounded">{state.procedureError}</div>
        )}
      </div>
    </form>
  );
};
